import React, { useCallback, useContext, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { DatasetType } from "@mui/x-charts/internals";
import {
    ResponsiveChartContainer,
    ChartsGrid,
    AreaPlot,
    LinePlot,
    ChartsAxisHighlight,
    LineHighlightPlot,
    ChartsXAxis,
    ChartsYAxis,
    LineSeriesType,
    BarSeriesType,
    BarPlot
} from "@mui/x-charts";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

import CustomLegend from "../CustomLegend";
import CustomAxisTooltip from "../CustomTooltip";
import { Bucket } from "../../../../../api/indicators";
import { UserContext } from "../../../../../context/UserContext";
import { ChartType } from "../../../../../models/dashboard";
import NavigationTooltip from "../NavigableTooltip";
import { yAxisFormatter } from "../../../dashboardHelpers";
import { useBrowserInfo } from "../../../../../helpers/useBrowserInfo";

interface LineChartBaseProps {
    bucket: Bucket;
    chartType: ChartType;
    series: (LineSeriesType | BarSeriesType)[];
    data: DatasetType;
    height: number;
    leftAxisLabel: string;
    rightAxisLabel?: string;
}

const LineBarChartBase = ({
    bucket,
    chartType,
    series,
    data,
    height,
    leftAxisLabel,
    rightAxisLabel
}: LineChartBaseProps) => {

    const theme = useTheme();
    const { locale } = useContext(UserContext);

    const [showNavigableLegend, setShowNavigableLegend] = useState(false);
    const [dataIndex, setDataIndex] = useState(0);

    const safari = useBrowserInfo().browser === 'Safari Browser';

    const normalizedData = useMemo(() =>
        (data as any[] ?? []).map((item) => {
            const raw = item.dateTime;

            let parsed: Date | null = null;

            if (raw instanceof Date) {
                parsed = raw;
            } else if (raw != null) {
                const candidate = new Date(String(raw).replace(" ", "T"));

                if (!Number.isNaN(candidate.getTime())) {
                    parsed = candidate;
                }
            }

            return {
                ...item,
                dateTime: parsed,
            };
        }),
        [data]
    );

    const adaptedSeries = useMemo(() => {
        if (!safari) return series;

        return series.map((s: any) => {
            if (s.type === "bar") {
                return {
                    ...s,
                    type: "line",
                    showMark: false,
                    curve: "linear"
                };
            }

            return s;
        });
    }, [series, safari]);

    const yAxisDomains = useMemo(() => {
        const values = normalizedData.flatMap((item: any) =>
            adaptedSeries
                .map((serie: any) => item[serie.dataKey])
                .filter((value) => typeof value === "number")
        );

        if (!values.length) {
            return {
                min: 0,
                max: 0,
            };
        }

        const minValue = Math.min(...values);
        const maxValue = Math.max(...values);

        const padding = (maxValue - minValue) * 0.1 || 1;

        return {
            min: minValue >= 0 ? 0 : minValue - padding,
            max: maxValue <= 0 ? 0 : maxValue + padding,
        };
    }, [normalizedData, adaptedSeries]);

    const formatAxisValue = useCallback((value: Date) => {
        let formatter: Intl.DateTimeFormat;

        if (bucket === "hour") {
            formatter = new Intl.DateTimeFormat(locale, {
                hour: "2-digit",
                minute: "2-digit"
            });
        } else if (bucket === "day") {
            formatter = new Intl.DateTimeFormat(locale, {
                month: "short",
                weekday: "short",
                day: "2-digit"
            });
        } else if (bucket === "week") {
            formatter = new Intl.DateTimeFormat(locale, {
                month: "short",
                day: "2-digit"
            });
        } else {
            formatter = new Intl.DateTimeFormat(locale);
        }

        return formatter.format(value);
    }, [bucket, locale]);

    const handleClose = () => setShowNavigableLegend(false);

    return (
        <>
            <Stack sx={{ width: "100%", marginTop: "20px" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography style={theme.typography.lineBarText}>
                        {leftAxisLabel}
                    </Typography>

                    {chartType === "line" && (
                        <Typography style={theme.typography.lineBarText}>
                            {rightAxisLabel}
                        </Typography>
                    )}
                </Box>

                <Box
                    onClick={() => setShowNavigableLegend(true)}
                    sx={{ width: "100%" }}
                >
                    <ResponsiveChartContainer
                        series={adaptedSeries}
                        dataset={normalizedData}
                        height={height * 0.7}
                        margin={{ top: 16, bottom: 32 }}
                        onHighlightChange={(el) => {
                            if (el?.dataIndex != null) {
                                setDataIndex(el.dataIndex);
                            }
                        }}
                        xAxis={[
                            {
                                id: "x-axis-bottom-id",
                                dataKey: "dateTime",
                                scaleType: "band",
                                valueFormatter: (v: Date) => {
                                    if (!v) return "";
                                    return formatAxisValue(v);
                                }
                            }
                        ]}
                        yAxis={[
                            {
                                id: "y-axis-left-id",
                                scaleType: "linear",
                                valueFormatter: yAxisFormatter,
                                min: yAxisDomains?.min,
                                max: yAxisDomains?.max,
                            },
                            {
                                id: "y-axis-right-id",
                                scaleType: "linear",
                                position: "right",
                                valueFormatter: yAxisFormatter,
                                min: yAxisDomains?.min,
                                max: yAxisDomains?.max,
                            }
                        ]}
                    >
                        <ChartsGrid horizontal />

                        <BarPlot />

                        <AreaPlot opacity={0.2} />

                        <LinePlot />

                        <CustomAxisTooltip />

                        <ChartsAxisHighlight x="line" />

                        <LineHighlightPlot />

                        <ChartsXAxis axisId="x-axis-bottom-id" />
                        <ChartsYAxis axisId="y-axis-left-id" />
                        <ChartsYAxis axisId="y-axis-right-id" />

                        <NavigationTooltip
                            open={showNavigableLegend && series?.length > 2}
                            onClose={handleClose}
                            dataset={normalizedData}
                            series={adaptedSeries}
                            seriesIndex={dataIndex}
                        />
                    </ResponsiveChartContainer>
                </Box>
            </Stack>

            <CustomLegend series={series} />
        </>
    );
};

export default LineBarChartBase;
